import assert from 'assert';
import {useState, useEffect} from 'react';
import classNames from 'classnames';
import sdk from '@/sdk';
import {getAttrs} from '@/utils/arrGetAttrUtil';

import Icon, {IconSize} from '@/components/Icon';
import Avatar, {AvatarSize} from '@/components/Avatar';
import DropMenu, {DropMenuTrigger, DropMenuSize} from '@/components/DropMenu';
import {SvgWorkflowMore} from '@baidu/luffy-icon';
import {LikeFillSvg} from '@/icons';
import CoinDeposit from '@/assets/svg/docCoinAndLiked.svg';
import UserInfoTooltip from '@/components/UserInfoTooltip';
import {feLogger, documentLike} from '@/buried';
import styles from './index.module.scss';
import UserList from '@/components/UserList';
import {isNull} from 'lodash-es';
// LikeSvg
interface LikeProps {
    docGuid: string;
    repository: Repository;
    className?: string;
}

const particlesConfig = {
    'fullScreen': {
        'zIndex': 1,
    },
    'particles': {
        'number': {
            'value': 0,
        },
        'color': {
            'value': [
                '#9ab9ff',
                '#db97ff',
                '#ff9999',
                '#9affc1',
                '#fdffa6',
            ],
        },
        'shape': {
            'type': [
                'triangle',
                'square',
            ],
            'options': {},
        },
        'opacity': {
            'value': {
                'min': 0,
                'max': 0.8,
            },
            'animation': {
                'enable': true,
                'speed': 2,
                'startValue': 'max',
                'destroy': 'min',
            },
        },
        'size': {
            'value': {
                'min': 4,
                'max': 6,
            },
        },
        'links': {
            'enable': false,
        },
        'life': {
            'duration': {
                'sync': true,
                'value': 3,
            },
            'count': 1,
        },
        'move': {
            'enable': true,
            'gravity': {
                'enable': true,
                'acceleration': 10,
                'maxSpeed': 100,
            },
            'speed': {
                'min': 30,
                'max': 60,
            },
            'decay': 0.07,
            'direction': 'top',
            'straight': false,
            'outModes': {
                'default': 'destroy',
                'top': 'none',
            },
        },
        'rotate': {
            'value': {
                'min': 0,
                'max': 360,
            },
            'direction': 'random',
            'move': true,
            'animation': {
                'enable': true,
                'speed': 60,
            },
        },
        'tilt': {
            'direction': 'random',
            'enable': true,
            'move': true,
            'value': {
                'min': 0,
                'max': 360,
            },
            'animation': {
                'enable': true,
                'speed': 60,
            },
        },
        'roll': {
            'darken': {
                'enable': true,
                'value': 25,
            },
            'enable': true,
            'speed': {
                'min': 15,
                'max': 25,
            },
        },
        'wobble': {
            'distance': 30,
            'enable': true,
            'move': true,
            'speed': {
                'min': -15,
                'max': 15,
            },
        },
    },
    'emitters': {
        'life': {
            'count': 1,
            'duration': 0.1,
            'delay': 0.0,
        },
        'domId': 'wiki-like-button',
        'rate': {
            'delay': 0.1,
            'quantity': 150,
        },
        'size': {
            'width': 0,
            'height': 0,
        },
    },
};

export default function Like(props: LikeProps) {
    const {repository, docGuid, className} = props;
    assert(repository);
    const {repositoryGuid} = repository;
    const userGuid = sdk.as.user?.userGuid;
    const [likeNum, setLikeNum] = useState(0);
    const [isLike, setIsLike] = useState(false);
    const [likeList, setLikeList] = useState<any[] | null>(null);
    // 对页面点赞
    async function handleGiveLikes() {
        try {
            const ksServer = await sdk.getKsServer(repository);
            await ksServer.giveLikes(docGuid);
            setIsLike(true);
            feLogger(documentLike({operateName: 'like', pageId: docGuid}));
        } catch (error) {
            // console.log(error);
        }
    }

    // 取消点赞
    async function handleCancelGiveLikes() {
        try {
            const ksServer = await sdk.getKsServer(repository);
            await ksServer.cancelGiveLikes(docGuid);
            setIsLike(false);
        } catch (error) {
            // console.log(error);
        }
    }

    function handleLikes() {
        if (isLike) {
            handleCancelGiveLikes();
            return;
        }

        // 碎纸动画
        // tsParticels 暂时从编辑器中加载，这里判断是否存在
        if ((window as any).tsParticles) {
            let particlesDOM = document.querySelector('#mp-tsparticles-container');
            if (!particlesDOM) {
                particlesDOM = document.createElement('div');
                particlesDOM.id = 'mp-tsparticles-container';
                document.body.appendChild(particlesDOM);
            }
            (window as any).tsParticles.load({
                id: 'mp-tsparticles-container',
                options: particlesConfig,
            });
        }

        handleGiveLikes();
    }

    // 获取赞列表
    useEffect(() => {
        // 点赞列表结构
        // eslint-disable-next-line func-names
        const likeListDom = (member: any) => {
            return (
                <div className={
                    styles.likesListWrap
                }
                >
                    <Avatar
                        size={AvatarSize.XS}
                        userGuid={member.userGuid}
                    />
                    <div className={
                        styles.likesListItemInfo
                    }
                    >
                        <p className={
                            styles.likesListItemTitle}
                        >
                            <span>
                                {member.nickname}
                            </span>
                            <span className={
                                styles.likesListItemDepart}
                            >
                                {member.department}
                            </span>
                        </p>
                        <p className={
                            styles.likesListItemEmail}
                        >
                            {member.email}
                        </p>
                    </div>
                </div>
            );
        };
        // eslint-disable-next-line complexity
        async function getLikesList() {
            try {
                const ksServer = await sdk.getKsServer(repository);
                let likeNumbers = 0;
                // 点赞列表的所有数据
                const likesList = await ksServer.getLikesList(docGuid);
                likeNumbers = likesList && likesList.length;
                setLikeNum(likeNumbers);
                // 判断当前用户是否已经进行点赞, 提升到获取用户信息之前
                if (likeNumbers) {
                    const isHasLikeArr = likesList.filter(item => {
                        return item.userGuid === userGuid;
                    });
                    if (isHasLikeArr && isHasLikeArr.length) {
                        setIsLike(true);
                    }
                }
                // 恢复原始状态
                else {
                    setIsLike(false);
                }

                // 拿到所有的Guid去批量请求完整的点赞列表的数据
                const userGuidList = getAttrs(likesList.reverse(), 'userGuid');
                // const showAchieves = isNull(isKMInGray) ? true : isKMInGray;
                const usersList = await sdk.as.getUserInfoList(userGuidList.slice(0, 200), false); // 截断取前200个
                const sortUsersList = (usersList: any[]) => {
                    return usersList.sort((a, b) => {
                        return userGuidList.indexOf(a.userGuid) - userGuidList.indexOf(b.userGuid);
                    });
                };
                const result = sortUsersList(usersList);
                // 本来是想数量超出分批请求的，因为目前没有分页，一次渲染太多头像存在性能问题，所以改成只展示前200个
                // const userGuidsList = [];
                // while (userGuidList.length > 300) {
                //     userGuidsList.push(userGuidList.splice(0, 300));
                // }
                // if (userGuidList.length) {
                //     userGuidsList.push(userGuidList);
                // }

                // const results = await Promise.all(userGuidsList.map((userGuids: string[]) => {
                //     return sdk.as.getUserInfoList(userGuids);
                // }));
                // let result: User[] = [];
                // results.forEach(userList => {
                //     result = result.concat(userList);
                // });

                // 设置DropMenu组件的数据格式
                if (result && result.length > 0) {
                    const options = result.map(member => {
                        return {
                            label: likeListDom(member),
                            value: member.userGuid,
                            name: member.avatar,
                        };
                    });
                    setLikeList(options);
                }
                else {
                    setLikeList([]);
                }
            } catch (error) {
                // console.error(error);
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getLikesList();
        // 根据isLike的更改做变化，因为isLike如果更改的话，自己进行了点赞/取消点赞的动作, 对应的赞列表也要做变化
    }, [docGuid, repositoryGuid, repository, isLike, userGuid]);
    // const panelNumClassName = isLike ? styles.likedNum : styles.beforeLikeNum;
    return (
        <div className={classNames(
            styles.likeWrap,
            className
        )}
        >
            {/* 点赞 */}
            <div className={
                styles.likeWrapCont}
            >
                <div
                    id="wiki-like-button"
                    className={classNames(
                        styles.likeIconBg,
                        isLike && styles.likeActiveIconBg
                    )}
                    onClick={handleLikes}
                >
                    <div>
                        <Icon
                            className={styles.likeDropShadow}
                            svg={LikeFillSvg}
                            size={IconSize.XXL}
                        />
                        {isLike && <img className={styles.coinDeposit} src={CoinDeposit} />}
                    </div>
                </div>
                {/* 不能通过isLike（是用来区分当前用户是否点赞）做区分展示，有点赞列表就需要展示 */}
                {
                    (likeList && likeList.length)
                        ? <p className={styles.likedTxt}><span className={styles.likedNum}>{likeNum}</span>人点赞</p>
                        : <p className={styles.likedTitle}>点赞</p>
                }
                <UserList userList={likeList} isLikeFrom />
            </div>
        </div>
    );
}
